import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";

// Import the App component lazily to improve initial page load
const App = lazy(() => import("./App.jsx"));

// Add a performance mark to measure initial load time
performance.mark("app-start");

// Create a simple loading component to show while the main app loads
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Define which features to load with priority
if ("connection" in navigator) {
  if (navigator.connection.saveData) {
    // Respect data-saving settings
    console.log("Using data-saving mode");
  }
}

// Add to browser history for analytics
window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${window.location.hash}`);

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <HelmetProvider>
      <Suspense fallback={<LoadingFallback />}>
        <App />
      </Suspense>
    </HelmetProvider>
  </StrictMode>
);

// Measure and log the performance for development analysis
window.addEventListener("load", () => {
  performance.mark("app-loaded");
  performance.measure("app-loading-time", "app-start", "app-loaded");

  // Only log in development
  if (import.meta.env.DEV) {
    const measure = performance.getEntriesByName("app-loading-time")[0];
    console.log(`App loaded in ${measure.duration.toFixed(2)}ms`);
  }

  // Report core web vitals metrics
  if ("web-vitals" in window) {
    import("web-vitals")
      .then(({ getCLS, getFID, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      })
      .catch((error) => {
        console.error("Failed to load web-vitals", error);
      });
  }
});
